import { Spacing } from '@persona-id/design-tokens';
import { ReactElement } from 'react';
import styled from 'styled-components';
import { v4 as uuid } from 'uuid';

import { ScreenFlowStepType } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateFlowEditor/types';
import { getDisplayNameForStep } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenEditor/utils';
import { PREVIEW_WIDTH } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenPreview/InquiryTemplateScreenPreview';
import { TreeReferenceStep } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateTree/InquiryTemplateTreeProvider';
import { stepTypeDisplayLabels } from 'dashboard/apps/InquiryTemplateEditor/ScreenFlowAddStepButton/ScreenFlowAddStepButton';
import {
  EXPAND_BACKGROUND_PADDING,
  EXPAND_COLLAPSED_CARD_FAN_WIDTH,
} from 'dashboard/apps/InquiryTemplateEditor/ScreenFlowNodeWrapper/ExpandableScreenFlowNodeWrapper';
import { WorkflowStep, WorkflowStepType } from 'dashboard/models/workflow-version';
import { paragraphMixins } from 'design-system/mixins/text';
import { Hairline } from 'design-system/variables/effect';
import { UiStepType } from 'lib/interfaces/StepsConfig';

/**
 * Dimensions, copy, and styled components shared
 * by InquiryTemplateTree and InquiryRunTree
 */
export const stepContentHeight = 770;
export const stepContentWidth = PREVIEW_WIDTH;
export const workflowErrorRouteLabel = 'Actions Error';
export const workflowErrorRouteDescription =
  'Fallback screen if the next action sequence experiences an error';

export const ConditionalWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
`;

export const Container = styled('nav')<{ width?: string }>`
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme.color.background.primary};
  width: ${({ width }) => width ?? '250px'};
  height: 100%;
  cursor: default;
`;

export const NavigationItemsContainer = styled('div')`
  padding: ${Spacing[4]};
  flex: auto;
  overflow-y: auto;
  margin-top: 1px;
`;

export const NavigationHeaderContainer = styled('div')`
  ${paragraphMixins.lgBold};
  display: flex;
  flex: none;
  flex-direction: column;
  padding: ${Spacing[4]} ${Spacing[5]};
  box-shadow: ${Hairline.Bottom};
`;

interface NavItemAttributes {
  testId: string;
  displayName: string;
  prefixIcon: ReactElement<any> | undefined;
}

export const getNavItemAttributes = (step: TreeReferenceStep | WorkflowStep): NavItemAttributes => {
  let testId = `inquiry-template-editor__tree-nav-item-${step.token}`;
  let displayName = '';
  let prefixIcon;
  switch (step.type) {
    case UiStepType.Document:
    case UiStepType.GovernmentId:
    case UiStepType.Integration:
    case UiStepType.VerifyWithPersona:
    case UiStepType.Selfie:
    case UiStepType.SelfieReflection:
    case UiStepType.Ui:
    case UiStepType.Loop: {
      displayName = getDisplayNameForStep(step);
      if (step.isConnector) {
        testId = `${testId}-connector-${uuid()}`;
        prefixIcon = stepTypeDisplayLabels[ScreenFlowStepType.Connector].icon;
      } else if (step.type === UiStepType.Integration) {
        prefixIcon = stepTypeDisplayLabels[ScreenFlowStepType.Integration].icon;
      } else {
        prefixIcon =
          step.type === UiStepType.Ui
            ? stepTypeDisplayLabels[ScreenFlowStepType.Screen].icon
            : stepTypeDisplayLabels[ScreenFlowStepType.Module].icon;
      }
      break;
    }
    case 'workflow':
      displayName = step.workflowStep.name;
      prefixIcon = stepTypeDisplayLabels[ScreenFlowStepType.Actions].icon;
      break;
    case WorkflowStepType.Action:
    case WorkflowStepType.Parallel:
    case WorkflowStepType.Placeholder:
    case WorkflowStepType.Wait:
      displayName = step.name;
      break;
    case UiStepType.Complete:
      displayName = getDisplayNameForStep(step);
      prefixIcon = stepTypeDisplayLabels[ScreenFlowStepType.Screen].icon;
      break;
    default:
      break;
  }
  return {
    testId,
    displayName,
    prefixIcon,
  };
};

export const calculateExpandableScreenFlowWidth = (stepContentWidth: number) => {
  return stepContentWidth + EXPAND_COLLAPSED_CARD_FAN_WIDTH + EXPAND_BACKGROUND_PADDING / 2;
};

export const calculateScreenFlowPadding = (screenHeight: number) => {
  // Padding between screen flow cards should be 1/4 of the screen height
  return screenHeight / 4;
};
